/* ═══════════════════════════════════════════════════════════════
   NOVA — VAULT GALLERY
   Desktop: ScrollTrigger pin + horizontal travel (if GSAP present)
   Mobile/fallback: native horizontal scroll container
   Reduced motion: disables pin/translate, keeps native scroll
   Shopify Theme Editor safe: init/cleanup on load/unload
   Integrates with NovaMotion.registerModule when available
   ═══════════════════════════════════════════════════════════════ */

(() => {
  const MODULE = 'vault-gallery';
  const SELECTOR = '[data-nova-vault-gallery]';

  const mqDesktop = () => window.matchMedia('(min-width: 990px)');
  const mqReduce = () => window.matchMedia('(prefers-reduced-motion: reduce)');

  function hasGSAP() {
    return !!(window.gsap && window.ScrollTrigger);
  }

  function safeParseFloat(v) {
    const n = Number.parseFloat(v);
    return Number.isFinite(n) ? n : 0;
  }

  function initSection(sectionEl) {
    if (!sectionEl || sectionEl.__novaVaultInit) return () => {};
    sectionEl.__novaVaultInit = true;

    const ac = new AbortController();
    const { signal } = ac;

    const viewport = sectionEl.querySelector('[data-vault-viewport]');
    const track = sectionEl.querySelector('[data-vault-track]');
    const btnPrev = sectionEl.querySelector('[data-vault-nav="prev"]');
    const btnNext = sectionEl.querySelector('[data-vault-nav="next"]');

    if (!viewport || !track) {
      return () => ac.abort();
    }

    let stTween = null;
    let stInstance = null;
    let isPinnedMode = false;

    function cleanupScrollTrigger() {
      isPinnedMode = false;
      if (stInstance) {
        try { stInstance.kill(true); } catch (_) {}
        stInstance = null;
      }
      if (stTween) {
        try { stTween.kill(); } catch (_) {}
        stTween = null;
      }
      // Reset transform so fallback scroll works if modes change
      track.style.transform = '';
    }

    function setupPinnedDesktop() {
      cleanupScrollTrigger();

      if (!hasGSAP()) return;
      if (mqReduce().matches) return;

      // If content doesn't overflow, don't pin
      const maxX = track.scrollWidth - viewport.clientWidth;
      if (maxX <= 8) return;

      const gsap = window.gsap;
      const ScrollTrigger = window.ScrollTrigger;

      // Ensure plugin registered (safe if repeated)
      try { gsap.registerPlugin(ScrollTrigger); } catch (_) {}

      isPinnedMode = true;

      const computeMaxX = () => track.scrollWidth - viewport.clientWidth;

      stTween = gsap.to(track, {
        x: () => -computeMaxX(),
        ease: 'none',
        overwrite: true
      });

      stInstance = ScrollTrigger.create({
        trigger: sectionEl,
        start: 'top top',
        end: () => `+=${Math.max(track.scrollWidth, viewport.clientWidth)}`,
        pin: true,
        pinSpacing: true,
        scrub: 1,
        anticipatePin: 1,
        invalidateOnRefresh: true,
        animation: stTween
      });

      // Refresh after images load (avoid wrong widths)
      const imgs = sectionEl.querySelectorAll('img');
      let pending = 0;
      imgs.forEach(img => {
        if (!img.complete) pending++;
      });
      if (pending > 0) {
        const onImg = () => {
          pending--;
          if (pending <= 0) {
            try { ScrollTrigger.refresh(); } catch (_) {}
          }
        };
        imgs.forEach(img => {
          if (!img.complete) img.addEventListener('load', onImg, { once: true, signal });
        });
      }
    }

    function setupFallbackScroll() {
      cleanupScrollTrigger();
      // native horizontal scroll on viewport
      viewport.style.overflowX = 'auto';
    }

    function scrollViewportBy(direction) {
      const amount = Math.max(240, Math.floor(viewport.clientWidth * 0.85));
      viewport.scrollBy({ left: amount * direction, behavior: 'smooth' });
    }

    function nudgePinnedScroll(direction) {
      if (!stInstance) return;

      // Move progress by ~15% per click
      const delta = 0.15 * direction;
      const next = Math.min(1, Math.max(0, stInstance.progress + delta));

      const start = safeParseFloat(stInstance.start);
      const end = safeParseFloat(stInstance.end);
      const y = start + (end - start) * next;

      if (window.NovaMotion && typeof window.NovaMotion.scrollTo === 'function') {
        window.NovaMotion.scrollTo(y, { immediate: false, lock: false });
      } else {
        window.scrollTo({ top: y, behavior: 'smooth' });
      }
    }

    function bindControls() {
      if (btnPrev) {
        btnPrev.addEventListener('click', () => {
          if (isPinnedMode) nudgePinnedScroll(-1);
          else scrollViewportBy(-1);
        }, { signal });
      }

      if (btnNext) {
        btnNext.addEventListener('click', () => {
          if (isPinnedMode) nudgePinnedScroll(1);
          else scrollViewportBy(1);
        }, { signal });
      }

      // Keyboard: arrow keys when viewport focused
      viewport.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          if (isPinnedMode) nudgePinnedScroll(-1);
          else scrollViewportBy(-1);
        }
        if (e.key === 'ArrowRight') {
          e.preventDefault();
          if (isPinnedMode) nudgePinnedScroll(1);
          else scrollViewportBy(1);
        }
      }, { signal });
    }

    function applyMode() {
      const desktop = mqDesktop().matches;
      const reduced = mqReduce().matches;

      // Desktop pinned mode only when desktop + not reduced + gsap exists
      if (desktop && !reduced && hasGSAP()) {
        setupPinnedDesktop();
      } else {
        setupFallbackScroll();
      }
    }

    bindControls();
    applyMode();

    // Respond to breakpoint / reduced motion changes
    const mqd = mqDesktop();
    const mqr = mqReduce();

    const onChange = () => applyMode();
    mqd.addEventListener?.('change', onChange);
    mqr.addEventListener?.('change', onChange);

    signal.addEventListener('abort', () => {
      mqd.removeEventListener?.('change', onChange);
      mqr.removeEventListener?.('change', onChange);
      cleanupScrollTrigger();
      sectionEl.__novaVaultInit = false;
    });

    return () => ac.abort();
  }

  function initAll(root = document) {
    const sections = Array.from(root.querySelectorAll(SELECTOR));
    const cleanups = [];

    sections.forEach((el) => {
      if (!el.__novaVaultCleanup) {
        el.__novaVaultCleanup = initSection(el);
      }
      cleanups.push(() => {
        try { el.__novaVaultCleanup && el.__novaVaultCleanup(); } catch (_) {}
        el.__novaVaultCleanup = null;
      });
    });

    return () => cleanups.forEach(fn => fn());
  }

  function destroyAll(root = document) {
    const sections = Array.from(root.querySelectorAll(SELECTOR));
    sections.forEach((el) => {
      if (el.__novaVaultCleanup) {
        try { el.__novaVaultCleanup(); } catch (_) {}
        el.__novaVaultCleanup = null;
      }
      el.__novaVaultInit = false;
    });
  }

  // NovaMotion integration (preferred)
  if (window.NovaMotion && typeof window.NovaMotion.registerModule === 'function') {
    window.NovaMotion.registerModule(
      MODULE,
      () => initAll(document),
      () => destroyAll(document)
    );
  } else {
    document.addEventListener('DOMContentLoaded', () => initAll(document), { once: true });
  }

  // Shopify Theme Editor lifecycle
  document.addEventListener('shopify:section:load', (e) => {
    const root = e.target;
    if (!root) return;
    initAll(root);
  });

  document.addEventListener('shopify:section:unload', (e) => {
    const root = e.target;
    if (!root) return;
    destroyAll(root);
  });
})();
